import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import cmsApi from "@/api/cms";
import SEO from "@/components/ui/SEO";
import Loading from "@/components/ui/Loading";
import NotFoundPage from "./NotFoundPage";
import { BlockRenderer } from "@/components/BlockRenderer";
import { Suspense } from "react";
import { useLanguage } from "@/context/LanguageContext";

interface CMSPageProps {
  slug?: string;
}

/**
 * Generic CMS page component that fetches and renders any page by slug
 */
const CMSPage: React.FC<CMSPageProps> = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = propSlug || paramSlug || "";
  const { language } = useLanguage();

  // Fetch page data from CMS
  const {
    data: page,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["page", slug, language],
    queryFn: () => cmsApi.getPage(slug, language),
    enabled: !!slug,
  });

  if (isLoading) {
    return <Loading fullScreen />;
  }

  if (error) {
    // Show different error messages based on error type
    const errorMessage = error as {
      status?: number;
      message?: string;
      details?: string;
    };

    if (import.meta.env.DEV) {
      // Show detailed error in development
      return (
        <div className="min-h-[600px] flex items-center justify-center">
          <div className="container mx-auto px-6 lg:px-12 max-w-2xl">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-red-800 mb-2">
                    Failed to Load Page: "{slug}"
                  </h3>
                  <div className="text-red-700 space-y-2">
                    <p>
                      <strong>Status:</strong>{" "}
                      {errorMessage.status || "Network Error"}
                    </p>
                    <p>
                      <strong>Message:</strong>{" "}
                      {errorMessage.message || "Unknown error"}
                    </p>
                    {errorMessage.details && (
                      <p>
                        <strong>Details:</strong> {errorMessage.details}
                      </p>
                    )}
                    <p className="mt-4 text-sm">
                      <strong>Troubleshooting:</strong>
                      <br />
                      1. Ensure the backend API is running at:{" "}
                      <code className="bg-red-100 px-1">
                        {import.meta.env.VITE_API_BASE_URL}
                      </code>
                      <br />
                      2. Check that the page "{slug}" exists in the CMS
                      <br />
                      3. Verify CORS settings on the backend
                      <br />
                      4. Check browser console for more details
                    </p>
                  </div>
                  <div className="mt-4">
                    <Link to="/" className="btn btn-primary btn-sm">
                      Go Back Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show user-friendly error in production
    return <NotFoundPage />;
  }

  if (!page) {
    return <NotFoundPage />;
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO
        title={page.metaTitle || page.title}
        description={page.metaDescription}
        keywords={page.metaKeywords}
        canonical={page.canonicalUrl}
        ogImage={page.ogImage}
        ogTitle={page.ogTitle}
        ogDescription={page.ogDescription}
        schemaMarkup={page.schemaMarkup}
      />

      <div className="cms-page">
        <Suspense
          fallback={<Loading fullScreen />}
        >
          {page.blocks
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((block) => {
              if (!block.isVisible) return null;

              let parsedContent: any = {};
              let renderKey = "";

              try {
                if (typeof block.content === "string") {
                  parsedContent = JSON.parse(block.content);
                } else if (typeof block.content === "object") {
                  parsedContent = block.content;
                }

                renderKey = parsedContent?.blockRenderKey || "";
              } catch (error) {
                renderKey = "INVALID_JSON";
              }

              return (
                <div key={block.id} data-block-id={block.id}>
                  <BlockRenderer
                    data={parsedContent.data || {}}
                    blockType={block.blockType}
                    blockRenderKey={renderKey}
                  />
                </div>
              );
            })}
        </Suspense>
      </div>
    </>
  );
};

export default CMSPage;
